'use client'

import confetti from 'canvas-confetti'
import { useEffect, type RefObject } from 'react'

const CONFETTI_COLORS = ['#E96DF2', '#8EE8CC', '#FFA75A', '#FFED8F', '#0676DE', '#B4F0DD', '#002140']

export type AmbientConfettiOptions = {
  /** How many fire() calls per interval tick when origins are not set. */
  burstCount?: number
  /** Fixed origin points (0–1). When set, one burst fires from each point. */
  burstOrigins?: Array<{ x: number; y: number }>
  burstIntervalMs?: number
  gravity?: number
  particleCount?: number
  scalar?: number
  startVelocity?: number
  ticks?: number
}

/** Tuned for full-section heroes (default). */
const HERO_OPTIONS: Required<Omit<AmbientConfettiOptions, 'burstOrigins'>> & {
  burstOrigins: AmbientConfettiOptions['burstOrigins']
} = {
  burstCount: 3,
  burstOrigins: undefined,
  burstIntervalMs: 900,
  gravity: 0.65,
  particleCount: 18,
  scalar: 0.95,
  startVelocity: 32,
  ticks: 320,
}

/**
 * Tuned for compact banners (e.g. filter promotions switch row).
 * Three fixed burst points across the banner width.
 */
export const COMPACT_AMBIENT_CONFETTI: Required<Omit<AmbientConfettiOptions, 'burstOrigins'>> & {
  burstOrigins: NonNullable<AmbientConfettiOptions['burstOrigins']>
} = {
  burstCount: 3,
  burstOrigins: [
    { x: 0.2, y: 0.5 },
    { x: 0.5, y: 0.45 },
    { x: 0.8, y: 0.5 },
  ],
  burstIntervalMs: 1400,
  gravity: 1.1,
  particleCount: 8,
  scalar: 0.75,
  startVelocity: 10,
  ticks: 140,
}

/**
 * Tuned for the landing promo banner — random burst points per tick, kept inside the card.
 */
export const PROMO_BANNER_MOBILE_BURST_COUNT = 4
export const PROMO_BANNER_DESKTOP_BURST_COUNT = 8
export const PROMO_BANNER_DESKTOP_MEDIA_QUERY = '(min-width: 768px)'

export const PROMO_BANNER_AMBIENT_CONFETTI: Required<
  Omit<AmbientConfettiOptions, 'burstOrigins'>
> & {
  burstOrigins: AmbientConfettiOptions['burstOrigins']
} = {
  burstCount: PROMO_BANNER_MOBILE_BURST_COUNT,
  burstOrigins: undefined,
  burstIntervalMs: 1400,
  gravity: 0.45,
  particleCount: 8,
  scalar: 0.75,
  startVelocity: 8,
  ticks: 180,
}

function randomBurstOrigin(): { x: number; y: number } {
  return {
    x: Math.random() * 0.7 + 0.15,
    y: Math.random() * 0.4 + 0.3,
  }
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

export function useAmbientConfetti(
  sectionRef: RefObject<HTMLElement | null>,
  options?: AmbientConfettiOptions,
): void {
  const {
    burstCount,
    burstOrigins,
    burstIntervalMs,
    gravity,
    particleCount,
    scalar,
    startVelocity,
    ticks,
  } = { ...HERO_OPTIONS, ...options }

  useEffect(() => {
    const section = sectionRef.current
    if (!section || prefersReducedMotion()) {
      return
    }

    const canvas = document.createElement('canvas')
    canvas.setAttribute('aria-hidden', 'true')
    canvas.className = 'pointer-events-none absolute inset-0 z-[1] size-full'
    canvas.classList.add('hidden')
    section.prepend(canvas)

    const fire = confetti.create(canvas, {
      resize: true,
      disableForReducedMotion: true,
    })

    let intervalId: ReturnType<typeof setInterval> | null = null
    let started = false
    let isIntersecting = false

    const burst = () => {
      if (!started || document.hidden) {
        return
      }

      const origins =
        burstOrigins && burstOrigins.length > 0
          ? burstOrigins
          : Array.from({ length: burstCount }, () => randomBurstOrigin())

      for (const origin of origins) {
        fire({
          particleCount,
          spread: 360,
          startVelocity,
          gravity,
          ticks,
          origin,
          colors: CONFETTI_COLORS,
          scalar,
          drift: Math.random() * 0.3 - 0.15,
        })
      }
    }

    const stopConfetti = () => {
      started = false
      if (intervalId) {
        clearInterval(intervalId)
        intervalId = null
      }
      fire.reset()
      canvas.classList.add('hidden')
    }

    const startConfetti = () => {
      canvas.classList.remove('hidden')
      if (started) {
        return
      }
      started = true
      requestAnimationFrame(() => {
        if (!started || document.hidden) {
          stopConfetti()
          return
        }

        burst()
        intervalId = setInterval(burst, burstIntervalMs)
      })
    }

    const syncConfetti = () => {
      if (isIntersecting && !document.hidden) {
        startConfetti()
        return
      }

      stopConfetti()
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        isIntersecting = entry.isIntersecting
        syncConfetti()
      },
      { threshold: 0 },
    )

    observer.observe(section)

    const rect = section.getBoundingClientRect()
    isIntersecting = rect.bottom > 0 && rect.top < window.innerHeight
    syncConfetti()

    document.addEventListener('visibilitychange', syncConfetti)

    return () => {
      document.removeEventListener('visibilitychange', syncConfetti)
      observer.disconnect()
      stopConfetti()
      canvas.remove()
    }
  }, [
    sectionRef,
    burstCount,
    burstOrigins,
    burstIntervalMs,
    gravity,
    particleCount,
    scalar,
    startVelocity,
    ticks,
  ])
}
